(() => {
  'use strict';

  if (!window.fetch || !window.Shopify?.routes?.root) return;

  const CONFIG_SELECTOR = '#distopion-auto-gift-config';
  const root = window.Shopify.routes.root;
  const source = 'distopion-free-gift';

  let config = null;
  let pendingCart = null;
  let running = false;
  let readScheduled = false;
  let protectionFrame = 0;
  let protectionTimer = 0;
  let settledResolvers = [];

  const endpoint = (path) => `${root}${path}`;

  const parseConfig = () => {
    const node = document.querySelector(CONFIG_SELECTOR);
    if (!node) return null;

    try {
      const raw = JSON.parse(node.textContent || '{}');
      const threshold = Number(raw.threshold || 0);
      const quantity = Math.max(1, Math.min(5, Math.round(Number(raw.quantity || 1))));

      return {
        enabled: raw.enabled === true,
        variantId: Number(raw.variantId || 0),
        handle: String(raw.handle || ''),
        threshold: Number.isFinite(threshold) ? threshold : 0,
        quantity,
        removeBelowThreshold: raw.removeBelowThreshold !== false,
        lockControls: raw.lockControls !== false,
        label: String(raw.label || ''),
        shopCurrency: String(raw.shopCurrency || '').toUpperCase(),
        initialItemCount: Number(raw.initialItemCount || 0),
      };
    } catch (error) {
      console.error('[Distopion automatic gift] Invalid configuration', error);
      return null;
    }
  };

  const refreshConfig = () => {
    config = parseConfig();
    requestProtection();
    return config;
  };

  const readJson = async (response) => {
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        payload?.description ||
          payload?.message ||
          `Cart request failed (${response.status})`
      );
    }

    return payload;
  };

  const getCart = async () => {
    const response = await fetch(endpoint('cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    return readJson(response);
  };

  const postCart = async (path, data) => {
    const response = await fetch(endpoint(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return readJson(response);
  };

  const isManagedGift = (item) =>
    String(item?.properties?._complimentary_gift || '') === 'true';

  const managedGiftLines = (cart) =>
    Array.isArray(cart?.items) ? cart.items.filter(isManagedGift) : [];

  const merchandiseSubtotal = (cart) => {
    if (!Array.isArray(cart?.items)) return 0;

    const merchandise = cart.items.reduce((total, item) => {
      if (isManagedGift(item)) return total;
      return total + Number(item.final_line_price || 0);
    }, 0);

    const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
      (total, discount) => total + Number(discount.total_allocated_amount || 0),
      0
    );

    return Math.max(0, merchandise - cartDiscount);
  };

  const thresholdForCart = (cart, activeConfig = config) => {
    if (!activeConfig?.shopCurrency || activeConfig.threshold <= 0) {
      return Number.POSITIVE_INFINITY;
    }

    const cartCurrency = String(cart?.currency || '').toUpperCase();

    if (cartCurrency === activeConfig.shopCurrency) return activeConfig.threshold;

    const activeCurrency = String(window.Shopify?.currency?.active || '').toUpperCase();
    const currencyRate = Number(window.Shopify?.currency?.rate);

    if (
      cartCurrency &&
      activeCurrency === cartCurrency &&
      Number.isFinite(currencyRate) &&
      currencyRate > 0
    ) {
      return Math.ceil(activeConfig.threshold * currencyRate);
    }

    return Number.POSITIVE_INFINITY;
  };

  const publish = (cart) => {
    const detail = { cart, source };

    document.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail,
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail,
      })
    );
  };

  const removeManagedGiftLines = async (lines) => {
    if (!lines.length) return;

    const updates = {};
    lines.forEach((line) => {
      updates[line.key] = 0;
    });

    await postCart('cart/update.js', { updates });
  };

  const normalizeGift = async (cart) => {
    const activeConfig = config;
    if (!activeConfig || !Array.isArray(cart?.items)) return null;

    const lines = managedGiftLines(cart);
    const featureReady =
      activeConfig.enabled &&
      activeConfig.variantId > 0 &&
      activeConfig.threshold > 0 &&
      activeConfig.shopCurrency;

    if (!featureReady) {
      if (!lines.length) return null;
      await removeManagedGiftLines(lines);
      return getCart();
    }

    const eligible = merchandiseSubtotal(cart) >= thresholdForCart(cart, activeConfig);

    if (!eligible) {
      if (!activeConfig.removeBelowThreshold || !lines.length) return null;
      await removeManagedGiftLines(lines);
      return getCart();
    }

    const alreadyCorrect =
      lines.length === 1 &&
      Number(lines[0].variant_id) === activeConfig.variantId &&
      Number(lines[0].quantity) === activeConfig.quantity;

    if (alreadyCorrect) return null;

    if (lines.length) await removeManagedGiftLines(lines);

    await postCart('cart/add.js', {
      items: [
        {
          id: activeConfig.variantId,
          quantity: activeConfig.quantity,
          properties: {
            _complimentary_gift: 'true',
            _automatic_gift_source: 'distopion',
          },
        },
      ],
    });

    return getCart();
  };

  const resolveSettled = () => {
    if (running || pendingCart || readScheduled) return;

    const resolvers = settledResolvers;
    settledResolvers = [];
    resolvers.forEach((resolve) => resolve());
  };

  const whenSettled = () => {
    if (!running && !pendingCart && !readScheduled) return Promise.resolve();
    return new Promise((resolve) => settledResolvers.push(resolve));
  };

  const drain = async () => {
    if (running) return;
    running = true;

    try {
      while (pendingCart) {
        const cart = pendingCart;
        pendingCart = null;

        try {
          const finalCart = await normalizeGift(cart);

          if (finalCart) {
            publish(finalCart);
            requestProtection();

            if (pendingCart) pendingCart = await getCart();
          }
        } catch (error) {
          console.error('[Distopion automatic gift]', error);
        }
      }
    } finally {
      running = false;

      if (pendingCart) {
        queueMicrotask(drain);
      } else {
        resolveSettled();
      }
    }
  };

  const enqueue = (cart) => {
    if (!cart?.items) {
      scheduleRead();
      return;
    }

    pendingCart = cart;
    queueMicrotask(drain);
  };

  const scheduleRead = (delay = 0) => {
    if (readScheduled) return;
    readScheduled = true;

    window.setTimeout(async () => {
      readScheduled = false;

      try {
        enqueue(await getCart());
      } catch (error) {
        console.error('[Distopion automatic gift]', error);
        resolveSettled();
      }
    }, delay);
  };

  const setGiftPriceLabel = (element, label) => {
    if (!element) return;

    if (!element.dataset.distopionOriginalText) {
      element.dataset.distopionOriginalText = element.textContent || '';
    }

    element.textContent = label || element.dataset.distopionOriginalText;
  };

  const setManagedControlState = (control, shouldHide) => {
    if (!control) return;

    if (shouldHide) {
      control.hidden = true;
      control.dataset.distopionGiftControlHidden = 'true';
    } else if (control.dataset.distopionGiftControlHidden === 'true') {
      control.hidden = false;
      delete control.dataset.distopionGiftControlHidden;
    }
  };

  const protectGiftControls = () => {
    if (!config?.handle) return;

    const escapedHandle = window.CSS?.escape ? CSS.escape(config.handle) : config.handle;
    const links = document.querySelectorAll(`a[href*="/products/${escapedHandle}"]`);

    links.forEach((link) => {
      const item = link.closest('line-item.line-item');
      if (!item) return;

      item.dataset.distopionFreeGift = 'true';

      item
        .querySelectorAll('.line-item__quantity-selector, quantity-selector, .line-item__remove-button')
        .forEach((control) => setManagedControlState(control, config.lockControls));

      setGiftPriceLabel(item.querySelector('.line-item__line-price'), config.label);

      const row = item.closest('tr');
      if (!row) return;

      row.dataset.distopionFreeGift = 'true';
      setManagedControlState(
        row.querySelector('td:nth-child(2) line-item'),
        config.lockControls
      );
      setGiftPriceLabel(row.querySelector('td:last-child span'), config.label);
    });
  };

  const requestProtection = () => {
    if (!config) return;

    if (!protectionFrame) {
      protectionFrame = requestAnimationFrame(() => {
        protectionFrame = 0;
        protectGiftControls();
      });
    }

    window.clearTimeout(protectionTimer);
    protectionTimer = window.setTimeout(protectGiftControls, 160);
  };

  const handleCartMutation = (event) => {
    requestProtection();
    if (event.detail?.source === source) return;
    enqueue(event.detail?.cart);
  };

  const start = () => {
    if (!refreshConfig()) return;

    document.addEventListener('cart:change', handleCartMutation, { passive: true });
    document.addEventListener('cart:refresh', requestProtection, { passive: true });

    document.addEventListener(
      'click',
      (event) => {
        if (!config?.lockControls) return;

        const protectedItem = event.target.closest('[data-distopion-free-gift="true"]');
        if (!protectedItem) return;

        const control = event.target.closest(
          'quantity-selector a, .line-item__remove-button, a[href*="/cart/change"]'
        );
        if (!control) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    document.addEventListener(
      'click',
      async (event) => {
        const checkout = event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (!checkout || (!running && !pendingCart && !readScheduled)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        await whenSettled();

        const form = checkout.form || checkout.closest('form');

        if (form?.requestSubmit) {
          form.requestSubmit(checkout);
        } else if (form) {
          form.submit();
        } else {
          location.href = `${root}checkout`;
        }
      },
      true
    );

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        if (!event.target.querySelector?.(CONFIG_SELECTOR)) return;
        refreshConfig();
        scheduleRead(0);
      },
      { passive: true }
    );

    if (config.initialItemCount > 0) {
      const initialCheck = () => scheduleRead();

      if ('requestIdleCallback' in window) {
        requestIdleCallback(initialCheck, { timeout: 1500 });
      } else {
        window.setTimeout(initialCheck, 600);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    refreshConfig();
    scheduleRead(0);
  });
})();
